/**
 * 자리수 고정(onAutoFixedDigit) 보조 — 저장된 «반올림본» 을 다른 자리수로 다시 반올림하는 것을 막는다.
 *
 * 배경(2026-09-29, EP-2609012 겉보기밀도): 시험일지 양식의 표시농도 자리수(2)로 1.447 이 1.45 로 저장되고,
 * 다시 열 때 서버가 행에 넣어 주는 마스터 digits(1)로 1.45 를 재반올림해 1.5 가 됐다(반올림 모드 half-up).
 * 원값 1.447 을 1자리로 반올림하면 1.4 가 맞다.
 *
 * 규칙: 수식 열이면 입력값으로 다시 계산한 원값(raw)을 쓰되, «저장값이 그 원값의 반올림본일 때만» 쓴다.
 *  - 저장값의 소수 자리수 d 로 raw 를 반올림한 값 == 저장값을 d 자리로 본 값 → 저장값은 반올림본 → raw 사용
 *  - 다르면(자동계산 OFF 에서 수기로 넣은 값 등) 저장값 유지
 *
 * 순수 함수라 webpack 과 node 테스트(scripts/*.test.js) 양쪽에서 쓴다 (journalDerive 와 같은 CJS 형식).
 */
const { toFixedHalfUp } = require('./round');

/** 숫자 문자열/숫자의 소수 자리수. 숫자로 볼 수 없으면 null. 지수 표기(1.2e-3)는 실제 자리수로 환산 */
function countDecimals(value) {
	if (value === null || value === undefined) return null;
	const s = String(value).trim();
	const m = /^[+-]?(\d+)?(?:\.(\d*))?(?:[eE]([+-]?\d+))?$/.exec(s);
	if (!m || (m[1] === undefined && (m[2] === undefined || m[2] === ''))) return null;
	let d = m[2] ? m[2].length : 0;
	if (m[3]) d = Math.max(0, d - Number(m[3]));
	return d;
}

/**
 * @param {*} stored   행에 저장된 값 (문자열/숫자)
 * @param {number} raw 수식으로 다시 계산한 원값
 * @param {(v:number, d:number)=>string} fixedFn 자리수 고정 함수 (ExcelTable.fixedValue 와 동일 규칙)
 * @returns {number|null} raw 를 써도 되면 raw, 아니면 null
 */
function pickRawForFixedDigit(stored, raw, fixedFn) {
	if (stored === null || stored === undefined || stored === '') return null;
	if (typeof raw !== 'number' || !Number.isFinite(raw)) return null;
	const storedNum = Number(stored);
	if (!Number.isFinite(storedNum)) return null;
	const d = countDecimals(stored);
	if (d === null) return null;
	if (String(fixedFn(raw, d)) !== String(fixedFn(storedNum, d))) return null;
	return raw;
}

/**
 * 반올림 결과가 0 인데 부호만 남은 «음수 0» 문자열(-0, -0.0, -0.00 …)에서 부호를 뗀다.
 *
 * 배경(2026-10-23, 노말헥산 EG-2610021): 바탕시료 무게차가 시료보다 조금 커서 결과가 -0.025 가 되면
 * Number#toFixed·Decimal#toFixed 둘 다 "-0.0" 을 낸다. 그 문자열이 그대로 저장돼 일지·엑셀에 «-0.0» 으로 나왔다.
 * 0 이 아닌 음수(-0.1 등)는 그대로 둔다. 문자열이 아니면 손대지 않는다.
 */
function stripNegativeZero(value) {
	if (typeof value !== 'string') return value;
	const t = value.trim();
	return /^-0(?:\.0+)?$/.test(t) ? t.slice(1) : value;
}

/**
 * 자리수 고정 문자열. 사사오입(utils/round.toFixedHalfUp) 이고 «-0.0» 대신 «0.0».
 * Number#toFixed 는 (20.65).toFixed(1) === '20.6' 처럼 부동소수 때문에 5 에서 내림되므로 쓰지 않는다.
 */
function toFixedStr(value, digits) {
	return stripNegativeZero(toFixedHalfUp(value, digits));
}

module.exports = { countDecimals, pickRawForFixedDigit, stripNegativeZero, toFixedStr };
